import type { IconName } from '@w7/ui'
import type { LegacyProfile } from './accessLevel'
import { landingMode, visibleModes } from './accessLevel'
import { modePath, WORKSPACES } from './routes'
import type { BookWorkspaceId, WorkspaceMode } from './domain'

export interface BookMenuChild {
  id: WorkspaceMode
  label: string
  to: string
  icon: IconName
}

export interface BookMenuGroup {
  id: BookWorkspaceId
  label: string
  /** Where the parent row goes — the first mode this profile may open. */
  to: string
  match: string
  aliases: string[]
  icon: IconName
  description: string
  children: BookMenuChild[]
}

const ICON: Record<BookWorkspaceId, IconName> = {
  'book-management': 'net-exposure',
  'premium-bm': 'sport',
}

/**
 * The rail dropdowns, filtered the way the legacy console filtered them.
 *
 * A workspace with no visible modes is dropped entirely rather than rendered
 * as an empty, un-openable row.
 */
export function bookMenuGroups(profile: LegacyProfile | null): BookMenuGroup[] {
  return WORKSPACES.flatMap((ws) => {
    const allowed = new Set(visibleModes(ws.id, profile))
    if (allowed.size === 0) return []

    const landing = landingMode(ws.id, profile)
    if (!landing) return []

    const children = ws.modes
      .filter((mode) => allowed.has(mode.id) && !mode.hiddenFromNav)
      .map((mode) => ({
        id: mode.id,
        label: mode.legacyName,
        to: modePath(ws.id, mode.id),
        icon: mode.icon,
      }))

    return [
      {
        id: ws.id,
        label: ws.menuLabel,
        to: modePath(ws.id, landing),
        match: ws.pathPrefix,
        aliases: ws.id === 'book-management' ? ['/net-exposure'] : [],
        icon: ICON[ws.id],
        description: children.map((child) => child.label).join(', '),
        children,
      },
    ]
  })
}
